import { IApiResponse, IComponent, IComponentProperty, IComponentPropertyType, ILayoutTemplate } from "../models/Interfaces";
import { CommonService } from "./CommonService";

export class SystemAdminService extends CommonService {

  public getAllComponents = async (): Promise<Array<IComponent>> => {
    const userid = this.userId;

    if (userid) {
      const url: string = "/Common/GetComponents";
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to retrieve component list");
      }
    }
    return [];
  }

  public getComponentPropertyTypes = async (): Promise<Array<IComponentPropertyType>> => {
    const userid = this.userId;

    if (userid) {
      const url: string = "/SystemAdmin/GetPropertyTypes";
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to retrieve component property types");
      }
    }
    return [];
  }

  public deleteComponent = async (componentid: number): Promise<boolean> => {
    if (componentid && this.userId) {
      const url: string = `/SystemAdmin/DeleteComponent/${componentid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to delete component");
      }
    }
    return false;
  }

  public deleteProperty = async (propid: number) : Promise<boolean> => {
    if (propid && this.userId) {
      const url: string = `/Systemadmin/DeleteProperty/${propid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);

      if (res?.StatusCode === 200)  {
        return true;
      } else {
        this.EmitDataError(res, "Failed to delete property");
      }
    }
    return false;
  }

  public saveComponent = async (component: IComponent): Promise<IComponent | null> => {
    if (component && this.userId) {
      const url: string = "/SystemAdmin/AddUpdateComponent";
      const model = {
        DisplayName: component.DisplayName,
        ExternalData: component.ExternalData,
        ExternalSource: component.ExternalSource,
        ExternalDataType: null,
        Id: component.Id,
        Name: component.Name,
        PropertyNames: component.Properties.map(p => { return { Id: p.Id, Name: p.PropertyName }})
      };

      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);
      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save component details");
      }
    }
    return null;
  }

  public saveComponentProperty = async (property: IComponentProperty) : Promise<IComponentProperty | null> => {
    if (property && property.Component && this.userId) {
      const url: string = "/SystemAdmin/AddUpdateProperty";
      const model = {
        ComponentId: property.Component.Id,
        DefaultValue: property.DefaultValue,
        DisplayLabel: property.DisplayLabel,
        External: property.External,
        Id: property.Id,
        MapFromPropertyName: property.MapFromPropertyName,
        Name: property.PropertyName,
        PropertyTypeId: property.Type.Id,
        ShowOnExtendedTab: property.ShowOnExtendedTab,
        UseFrameworkHelper: property.UseFrameworkHelper ?? false,
        UserEditable: property.UserEditable,
        ValidationErrorMessage: property.ValidationErrorMessage,
        ValidatorRegEx: property.ValidatorRegEx
      };

      const res = await this._ajaxService.Post<IApiResponse>(url, model, true);
      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save property details");
      }
    }
    return null;
  }

  public savePropertyType = async (type: IComponentPropertyType) : Promise<IComponentPropertyType | null> => {
    if (type && this.userId) {
      const url: string = "/SystemAdmin/AddUpdatePropertyType";
      const res = await this._ajaxService.Post<IApiResponse>(url, type, true);
      if (res?.ReturnData === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save property type details");
      }
    }
    return null;
  }

  public deletePropertyType = async (typeid: number) : Promise<boolean> => {
    if (typeid && this.userId) {
      const url: string = `/SystemAdmin/DeletePropertyType/${typeid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to delete property type");
      }
    }
    return false;
  }

  public getLayoutTemplates = async () : Promise<Array<ILayoutTemplate>> => {
    if (this.userId) {
      const url: string = "/Common/GetLayoutTemplates";
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        if (res?.StatusCode !== 404) {
          this.EmitDataError(res, "Failed to retrieve layout templates");
        }
      }
    }
    return [];
  }

  public saveLayoutTemplate = async (template: ILayoutTemplate) : Promise<ILayoutTemplate | null> => {
    if (this.userId && template) {
      const url: string = "/SystemAdmin/AddUpdateLayoutTemplate";
      const res = await this._ajaxService.Post<IApiResponse>(url, template, true);

      if (res?.StatusCode === 200 && res.ReturnData) {
        return res.ReturnData;
      } else {
        this.EmitDataError(res, "Failed to save layout template");
      }
    }
    return null;
  }

  public deleteLayoutTemplate = async (templateid: number) : Promise<boolean> => {
    if (templateid && this.userId) {
      const url: string = `/SystemAdmin/DeleteLayoutTemplate/${templateid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to delete layout template");
      }
    }
    return false;
  }

  public movePropertyUp = async (componentid: number, propertyid: number): Promise<boolean> => {
    if (componentid && propertyid && this.userId) {
      const url = `/SystemAdmin/MovePropertyUp/${componentid}/${propertyid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to move property up");
      }
    }
    return false;
  }

  public movePropertyDown = async (componentid: number, propertyid: number): Promise<boolean> => {
    if (componentid && propertyid && this.userId) {
      const url = `/SystemAdmin/MovePropertyDown/${componentid}/${propertyid}`;
      const res = await this._ajaxService.Get<IApiResponse>(url, true);
      if (res?.StatusCode === 200) {
        return true;
      } else {
        this.EmitDataError(res, "Failed to move property down");
      }
    }
    return false;
  }

  public newComponent = () : IComponent => {
    return {
      DisplayName: "",
      ExternalData: false,
      ExternalSource: "",
      ExternalType: null,
      ExternalTypeDescription: "",
      Name: "",
      Properties: [],
      Id: 0
    };
  }

  public newPropertyType = () : IComponentPropertyType => {
    return {
      Id: 0,
      Name: "",
      Type: ""
    };
  }

  public newTemplate = () : ILayoutTemplate => {
    return {
      Id: 0,
      ColumnBased: false,
      Description: "",
      DisplayOrder: 0,
      Enabled: true,
      TemplateName: ""
    };
  }

}
